import type { ClientData } from '@/lib/supabase-marketing';

// Re-export so callers only need this module.
export type { ClientData };

const CLIENT_KEY = 'perch_client';

// perch-marketing and perch-mobile share one origin split only by CloudFront
// path-based routing (see src/lib/cross-surface.ts), so a path=/ cookie set
// here is visible to perch-mobile too. 30 days mirrors useAffiliateTracking's
// attribution window.
const CLIENT_OVERRIDE_COOKIE_NAME = 'perch_client_override';
const CLIENT_OVERRIDE_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

// VITE_DEFAULT_CLIENT_ID must be the UUID of the default client row in the
// clients table (e.g. "591b3b1f-63be-41a5-afee-c84650ca5927"). Update your
// .env accordingly.
export const DEFAULT_CLIENT_ID: string =
  import.meta.env.VITE_DEFAULT_CLIENT_ID ?? '';

// Persists the raw ?client= override (not DEFAULT_CLIENT_ID) so it survives
// a full-page hop to perch-mobile and back once sessionStorage is gone.
export function writeClientOverrideCookie(id: string): void {
  try {
    // Secure is conditional so this still works over http://localhost in
    // dev; staging/production are https-only, so it's always set there.
    const secure =
      typeof location !== 'undefined' && location.protocol === 'https:'
        ? '; Secure'
        : '';
    document.cookie = `${CLIENT_OVERRIDE_COOKIE_NAME}=${encodeURIComponent(id)}; path=/; max-age=${CLIENT_OVERRIDE_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  } catch {
    // Storage blocked (Safari Lockdown, private mode) — degrade silently.
  }
}

// Returns the previously-stored raw override id, or null. Re-bounded to 128
// chars in case of a tampered cookie value.
export function readClientOverrideCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${CLIENT_OVERRIDE_COOKIE_NAME}=([^;]*)`),
  );
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]).slice(0, 128) || null;
  } catch {
    return null;
  }
}

// Write the DB-resolved client row to sessionStorage.
export function writeClientData(data: ClientData): void {
  try {
    sessionStorage.setItem(CLIENT_KEY, JSON.stringify(data));
  } catch {
    // Storage blocked (Safari Lockdown, private mode) — degrade silently.
  }
}

// Returns the cached client data, or null if not yet resolved or storage
// is unavailable. By the time the user reaches /plan-summary and clicks
// Continue, useClientTracking will have resolved and written this.
export function getClientConfig(): ClientData | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(CLIENT_KEY);
    return raw ? (JSON.parse(raw) as ClientData) : null;
  } catch {
    return null;
  }
}
